import React, { useContext, useMemo, useState, useEffect, useRef } from "react";
import { useSearchParams, useParams, useNavigate } from "react-router-dom";
import { buildListRoute, parseRouteEntityId } from "../../utils";
import { gql, useQuery, useMutation } from "@apollo/client";
import StarekContext from "../../context/StarekContext";
import { usePermissions } from "../../hooks/usePermissions";
import EntityLoadingState from "../../components/EntityLoadingState";
import EntityEmptyState from "../../components/EntityEmptyState";
import { EMPTY_MESSAGES } from "../../constants/emptyStateMessages";
import ConfirmModal from "../../components/modals/ConfirmModal";
import EntityOperationBar from "../../components/EntityOperationBar";
import MaintenanceSchedulesCalendarView from "./MaintenanceSchedulesCalendarView";
import MaintenanceSchedulesListingView from "./MaintenanceSchedulesListingView";
import MaintenanceScheduleSidebar from "./MaintenanceScheduleSidebar";
import { deactivateData, restoreData } from "../../utils/softDelete";
import usePagedList, { sortToOrderBy } from "../../hooks/usePagedList";
import Paginator from "../../components/Paginator";

const UI_STRINGS = {
    PAGE_TITLE: "Maintenance Schedules",
    // Short noun for the toolbar buttons ("Add Maintenance", "Delete 2 Maintenance"):
    // the full name pushes the button row out of line on smaller screens.
    TOOLBAR_ENTITY: "Maintenance",
    SEARCH_PLACEHOLDER: "Search maintenance schedules…",
    DELETE_TITLE_SINGLE: (name) => `Delete Maintenance Schedule ${name}?`,
    DELETE_TITLE_BULK: (count) => `Delete ${count} Maintenance Schedules?`,
    DELETE_MESSAGE_SINGLE: (
        name,
    ) => `You are about to delete maintenance schedule "${name}".

This action cannot be undone.

Are you sure you wish to continue?`,
    DELETE_MESSAGE_BULK: (
        count,
    ) => `You are about to delete ${count} maintenance schedule${count !== 1 ? "s" : ""}.

This action cannot be undone.

Are you sure you wish to continue?`,
    DELETE_CONFIRM: "Delete",
    DELETE_CANCEL: "Cancel",
    TOTAL_SELECTED: (count) => `Total maintenance schedules selected: ${count}`,
    NOT_FOUND: "Maintenance schedule not found",
};

// Schedule id for any row shape on this page: listing rows ({ id: "<device>-<schedule>",
// scheduleId }), calendar popup ({ deviceId, schedule }) and plain schedules ({ id }).
const scheduleIdOf = (row) =>
    Number(row?.scheduleId ?? row?.schedule?.id ?? row?.id);

// Everything the table and the sidebar read off a schedule read at the top level (list
// view and deep links), with its device inline.
const SCHEDULE_FIELDS = `
    id
    name
    description
    is_active
    schedule_type
    schedule_attributes
    device_id
    device {
        id
        name
    }
    created_at
    updated_at
    created_by {
        id
        name
    }
    updated_by {
        id
        name
    }
`;

/**
 * Table sort -> Prisma orderBy. The Device column sorts by the device's name through
 * the relation; From / To / When live inside the schedule_attributes JSON and cannot
 * be sorted on the server, so the table does not offer them.
 */
const scheduleOrderBy = (sort) => {
    if (!sort?.field) return [];
    if (sort.field === "device") {
        const direction = sort.direction === "desc" ? "desc" : "asc";
        return [{ device: { name: { sort: direction } } }];
    }
    return sortToOrderBy(sort, ["description"]);
};

const MaintenanceSchedules = (props) => {
    const starekContext = useContext(StarekContext);
    const { can } = usePermissions();
    const canWrite = can("maintenance_schedule", "WRITE");
    const canDestroy = can("maintenance_schedule", "DESTROY");
    const navigate = useNavigate();
    const { id: routeId } = useParams();
    const routeScheduleId = parseRouteEntityId(routeId);
    const [searchParams, setSearchParams] = useSearchParams();
    // Read the view straight from the URL on the first render, so a ?view=list link
    // does not fire the calendar's full query first.
    const [viewMode, setViewMode] = useState(() =>
        searchParams.get("view") === "list" ? "list" : "calendar",
    );
    const isListView = viewMode === "list";

    // View state lives in the URL so a refresh or deep link restores the same list.
    const showInactive = searchParams.get("showInactive") === "1";
    const patchSearchParams = (patch, options = {}) => {
        const next = new URLSearchParams(searchParams);
        Object.entries(patch).forEach(([key, value]) => {
            if (value === null || value === undefined || value === "") {
                next.delete(key);
            } else {
                next.set(key, value);
            }
        });
        setSearchParams(next, options);
    };
    const setShowInactive = (value) =>
        patchSearchParams({ showInactive: value ? "1" : null });
    const [selectedRows, setSelectedRows] = useState([]);

    useEffect(() => {
        const viewParam = searchParams.get("view");
        if (viewParam && (viewParam === "calendar" || viewParam === "list")) {
            setViewMode(viewParam);
        }
    }, [searchParams]);
    const [filterText, setFilterText] = useState("");
    const [toggleCleared, setToggleCleared] = useState(false);
    const [showSidebar, setShowSidebar] = useState(false);
    const [routeEditSchedule, setRouteEditSchedule] = useState(null);
    const [confirmCfg, setConfirmCfg] = useState({ open: false });
    const [isDeleting, setIsDeleting] = useState(false);
    const [deleteDone, setDeleteDone] = useState(0);
    // Ids just deleted from the sidebar: the post-delete refetch drops them from the
    // list while the route still points at them, which must not read as "not found".
    const deletedIdsRef = useRef(new Set());

    const GET_DEVICES_IN_PLANT = gql`
        query GetDevicesForPlant(
            $where: ShadowDeviceWhereInput
            $scheduleWhere: MaintenanceScheduleWhereInput
        ) {
            shadowDevices(where: $where) {
                id
                name
                maintenance_schedules(where: $scheduleWhere) {
                    id
                    name
                    description
                    is_active
                    schedule_type
                    schedule_attributes
                    created_at
                    updated_at
                    created_by {
                        id
                        name
                    }
                    updated_by {
                        id
                        name
                    }
                }
            }
        }
    `;

    // One page of schedules plus the total, for the list view (usePagedList).
    const GET_MAINTENANCE_SCHEDULES_PAGE = gql`
        query GetMaintenanceSchedulesPage(
            $where: MaintenanceScheduleWhereInput!
            $orderBy: [MaintenanceScheduleOrderByWithRelationInput!]
            $take: Int
            $skip: Int
        ) {
            maintenanceSchedules(
                where: $where
                orderBy: $orderBy
                take: $take
                skip: $skip
            ) {
                ${SCHEDULE_FIELDS}
            }
            aggregateMaintenanceSchedule(where: $where) {
                _count {
                    _all
                }
            }
        }
    `;

    // A deep link (/maintenance/:id) may point at a schedule that is not on the current
    // page, or is inactive while inactive schedules are hidden.
    const GET_MAINTENANCE_SCHEDULE_BY_ID = gql`
        query GetMaintenanceScheduleById($where: MaintenanceScheduleWhereInput!) {
            findFirstMaintenanceSchedule(where: $where) {
                ${SCHEDULE_FIELDS}
            }
        }
    `;

    const CREATE_ONE_MAINTENANCE_SCHEDULE = gql`
        mutation CreateOneMaintenanceSchedule(
            $data: MaintenanceScheduleCreateInput!
        ) {
            createOneMaintenanceSchedule(data: $data) {
                id
                name
                description
                schedule_type
                schedule_attributes
                device_id
            }
        }
    `;

    const UPDATE_ONE_MAINTENANCE_SCHEDULE = gql`
        mutation UpdateOneMaintenanceSchedule(
            $data: MaintenanceScheduleUpdateInput!
            $where: MaintenanceScheduleWhereUniqueInput!
        ) {
            updateOneMaintenanceSchedule(data: $data, where: $where) {
                id
                name
                description
                schedule_type
                schedule_attributes
                device_id
            }
        }
    `;

    const UPDATE_MANY_MAINTENANCE_SCHEDULE = gql`
        mutation UpdateManyMaintenanceSchedule(
            $data: MaintenanceScheduleUpdateManyMutationInput!
            $where: MaintenanceScheduleWhereInput
        ) {
            updateManyMaintenanceSchedule(data: $data, where: $where) {
                count
            }
        }
    `;

    const DELETE_ONE_MAINTENANCE_SCHEDULE = gql`
        mutation DeleteOneMaintenanceSchedule($id: Int!) {
            deleteOneMaintenanceSchedule(where: { id: $id }) {
                id
            }
        }
    `;

    const plantId = starekContext.lastUsedPlant?.id;
    const where = useMemo(
        () => (plantId ? { plant_id: { equals: Number(plantId) } } : undefined),
        [plantId],
    );

    // Schedules are read through the device relation, and the backend's soft-delete
    // middleware only scopes top-level reads — so the nested list must exclude deleted
    // rows itself, or a deleted schedule keeps showing up.
    const scheduleWhere = {
        deleted_at: { equals: null },
        ...(showInactive ? {} : { is_active: { equals: true } }),
    };

    // Calendar view: every device of the plant with its schedules. Only run while the
    // calendar is showing — the list view reads its own page below.
    const {
        data,
        previousData,
        loading: fullLoading,
        error: fullError,
        refetch: refetchFull,
    } = useQuery(GET_DEVICES_IN_PLANT, {
        variables: { where, scheduleWhere },
        client: starekContext.engineClient,
        skip: isListView,
        fetchPolicy: "network-only",
        nextFetchPolicy: "network-only",
    });
    const fullShown = data ?? previousData;

    // List view: the same schedules read at the top level. Same filters as the calendar:
    // this plant, not archived, active unless Show Inactive, and not on an archived
    // device (the calendar only reaches schedules through live devices).
    const pagedWhere = useMemo(
        () => ({
            plant_id: { equals: Number(plantId) },
            deleted_at: { equals: null },
            device: { is: { deleted_at: { equals: null } } },
            ...(showInactive ? {} : { is_active: { equals: true } }),
        }),
        [plantId, showInactive],
    );

    // List view: table sort, applied on the server ({ field, direction } or null).
    const [sort, setSort] = useState(null);
    const orderBy = useMemo(() => scheduleOrderBy(sort), [sort]);

    // List view: one page (12) of schedules from the server, searched and sorted there.
    const {
        items: pageItems,
        total,
        page,
        pageCount,
        pageSize,
        setPage,
        loading: pageLoading,
        initialLoading: pageInitialLoading,
        error: pageError,
        refetch: refetchPage,
    } = usePagedList({
        query: GET_MAINTENANCE_SCHEDULES_PAGE,
        listField: "maintenanceSchedules",
        countField: "aggregateMaintenanceSchedule",
        where: pagedWhere,
        searchText: filterText,
        orderBy,
        client: starekContext.engineClient,
        skip: !isListView || !plantId,
    });

    // /maintenance/:id may point at a schedule that is not on this page (list view) or
    // is inactive while inactive schedules are hidden, so it is loaded on its own.
    const {
        data: routeScheduleData,
        loading: routeScheduleLoading,
        refetch: refetchRouteSchedule,
    } = useQuery(GET_MAINTENANCE_SCHEDULE_BY_ID, {
        client: starekContext.engineClient,
        skip: routeScheduleId == null || !plantId,
        variables: {
            where: {
                id: { equals: routeScheduleId },
                plant_id: { equals: Number(plantId) },
                deleted_at: { equals: null },
            },
        },
        fetchPolicy: "network-only",
    });

    const error = isListView ? pageError : fullError;

    // Selection is per page: moving pages clears it.
    useEffect(() => {
        setSelectedRows([]);
        setToggleCleared((p) => !p);
    }, [page]);

    const [createMaintenanceSchedule] = useMutation(
        CREATE_ONE_MAINTENANCE_SCHEDULE,
        {
            client: starekContext.engineClient,
        },
    );
    const [updateMaintenanceSchedule] = useMutation(
        UPDATE_ONE_MAINTENANCE_SCHEDULE,
        {
            client: starekContext.engineClient,
        },
    );
    const [updateManyMaintenanceSchedule] = useMutation(
        UPDATE_MANY_MAINTENANCE_SCHEDULE,
        {
            client: starekContext.engineClient,
        },
    );
    const [deleteOneMaintenanceSchedule] = useMutation(
        DELETE_ONE_MAINTENANCE_SCHEDULE,
        {
            client: starekContext.engineClient,
        },
    );

    const devices = useMemo(() => fullShown?.shadowDevices || [], [fullShown]);

    /** The routed schedule among the rows the active view shows, as { ...schedule, device_id }. */
    const findLoadedSchedule = (id) => {
        if (isListView) {
            const sch = pageItems.find((s) => Number(s.id) === id);
            return sch ? { ...sch, device_id: sch.device_id ?? sch.device?.id } : null;
        }
        for (const device of devices) {
            const sch = (device.maintenance_schedules || []).find(
                (s) => Number(s.id) === id,
            );
            if (sch) return { ...sch, device_id: device.id };
        }
        return null;
    };

    // updateMany and delete only return a count or id, so the list has to be re-read
    // after a write. Only the active view's query is running, so only that one is
    // refetched. A failed refetch must not surface as a failed write.
    // After any write, re-read the open record too (unless it was just deleted): the
    // page rows from before the refetch cannot tell whether it is still on the page, and
    // updateMany returns only a count, so the cached by-id copy would otherwise go stale.
    const hardRefresh = async () => {
        try {
            await (isListView ? refetchPage() : refetchFull());
            // The open schedule may not be in the active rows; re-read it so the
            // sidebar sees the write too. A just-deleted one is skipped — it would read
            // as "not found".
            if (
                routeScheduleId != null &&
                !deletedIdsRef.current.has(routeScheduleId)
            ) {
                await refetchRouteSchedule();
            }
        } catch (err) {
            console.error("Failed to refresh maintenance schedules:", err);
        }
    };

    useEffect(() => {
        if (!routeId) {
            setRouteEditSchedule(null);
            return;
        }

        if (routeScheduleLoading) return;

        const listRoute = buildListRoute("/maintenance", searchParams);

        if (routeScheduleId == null) {
            starekContext.showError("Maintenance schedule not found");
            setRouteEditSchedule(null);
            navigate(listRoute, { replace: true });
            return;
        }

        // Just deleted from the sidebar: the sidebar closes itself. Checked before the
        // lookup, because the by-id result can still hold the deleted row for a moment.
        if (deletedIdsRef.current.has(routeScheduleId)) return;

        // Prefer the row the active view shows (fresh after every refresh); otherwise
        // the schedule loaded by id. Either way an inactive schedule opens without
        // widening the list.
        const byId = routeScheduleData?.findFirstMaintenanceSchedule;
        const scheduleWithDevice =
            findLoadedSchedule(routeScheduleId) ||
            (byId ? { ...byId, device_id: byId.device_id ?? byId.device?.id } : null);

        if (!scheduleWithDevice) {
            starekContext.showError(UI_STRINGS.NOT_FOUND);
            setRouteEditSchedule(null);
            navigate(listRoute, { replace: true });
            return;
        }

        setRouteEditSchedule(scheduleWithDevice);
        setSelectedRows((current) =>
            current.length === 1 && Number(current[0]?.id) === routeScheduleId
                ? current
                : [scheduleWithDevice]
        );
        setShowSidebar(true);
        // findLoadedSchedule is rebuilt every render; what it reads (isListView,
        // pageItems, devices) is listed instead.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        routeId,
        routeScheduleId,
        routeScheduleLoading,
        routeScheduleData,
        isListView,
        pageItems,
        devices,
        searchParams,
        navigate,
        starekContext,
    ]);

    // The full-page loader is for the page's first load only (see the render below).
    const activeInitialLoading = isListView
        ? pageInitialLoading
        : fullLoading && !fullShown;
    const [hasLoaded, setHasLoaded] = useState(false);
    useEffect(() => {
        if (!activeInitialLoading) setHasLoaded(true);
    }, [activeInitialLoading]);

    const handleRowClicked = (row) => {
        navigate(buildListRoute(`/maintenance/${row.scheduleId}`, searchParams));
    };

    const handleAdd = () => {
        setSelectedRows([]);
        setShowSidebar(true);
    };

    const handleEdit = () => {
        if (selectedRows.length !== 1) return;
        navigate(buildListRoute(`/maintenance/${scheduleIdOf(selectedRows[0])}`, searchParams));
    };

    // The calendar popup passes { deviceId, schedule }, not a listing row.
    const handleEditSchedule = (scheduleRow) => {
        const id = scheduleIdOf(scheduleRow);
        if (!Number.isFinite(id)) return;
        navigate(buildListRoute(`/maintenance/${id}`, searchParams));
    };

    const handleSidebarClose = async () => {
        setShowSidebar(false);
        setRouteEditSchedule(null);
        setSelectedRows([]);
        setToggleCleared((p) => !p);
        if (routeId) {
            navigate(buildListRoute("/maintenance", searchParams), {
                replace: true,
            });
        }
        await hardRefresh();
    };

    const openConfirm = (cfg) => setConfirmCfg({ open: true, ...cfg });
    const closeConfirm = () => setConfirmCfg({ open: false });

    const handleSaveMaintenanceSchedule = async (schedulePayload) => {
        try {
            const currentUserId =
                starekContext?.currentUser?.id ||
                starekContext?.user?.id ||
                starekContext?.me?.id ||
                2;

            const scheduleAttributesObj = {
                from: schedulePayload.from,
                to: schedulePayload.to,
            };

            if (!schedulePayload.id) {
                // Create
                await createMaintenanceSchedule({
                    variables: {
                        data: {
                            schedule_type: 2,
                            name: schedulePayload.name,
                            description: schedulePayload.description || "",
                            schedule_attributes: JSON.stringify(
                                scheduleAttributesObj,
                            ),
                            plant: {
                                connect: {
                                    id: starekContext.lastUsedPlant.id,
                                },
                            },
                            device: {
                                connect: {
                                    id: schedulePayload.deviceId,
                                },
                            },
                            created_by: {
                                connect: { id: Number(currentUserId) },
                            },
                            updated_by: {
                                connect: { id: Number(currentUserId) },
                            },
                        },
                    },
                });
            } else {
                // Update
                await updateMaintenanceSchedule({
                    variables: {
                        where: { id: schedulePayload.id },
                        data: {
                            name: { set: schedulePayload.name },
                            description: {
                                set: schedulePayload.description || "",
                            },
                            device: {
                                connect: {
                                    id: schedulePayload.deviceId,
                                },
                            },
                            schedule_attributes: scheduleAttributesObj,
                            updated_by: {
                                connect: { id: Number(currentUserId) },
                            },
                        },
                    },
                });
            }

            await handleSidebarClose();
            starekContext.showSuccess(
                schedulePayload.id
                    ? `Maintenance Schedule "${schedulePayload.name}" updated successfully`
                    : `Maintenance Schedule "${schedulePayload.name}" created successfully`,
            );
        } catch (error) {
            starekContext.showError(error);
            throw error;
        }
    };

    // Toolbar delete and sidebar delete both confirm here. `onSettled(ok)` lets the
    // sidebar stay open until the dialog is answered (true only after a full delete).
    const handleDelete = (schedules, onSettled) => {
        if (!schedules || schedules.length === 0 || isDeleting) {
            onSettled?.(false);
            return;
        }

        const isSingle = schedules.length === 1;
        const scheduleName =
            schedules[0]?.schedule?.name || schedules[0]?.name || "Unnamed";
        const title = isSingle
            ? UI_STRINGS.DELETE_TITLE_SINGLE(scheduleName)
            : UI_STRINGS.DELETE_TITLE_BULK(schedules.length);

        const message = isSingle
            ? UI_STRINGS.DELETE_MESSAGE_SINGLE(scheduleName)
            : UI_STRINGS.DELETE_MESSAGE_BULK(schedules.length);

        openConfirm({
            title: title,
            content: (
                <>
                    <div
                        className="text-dark"
                        style={{ whiteSpace: "pre-wrap" }}
                    >
                        {message}
                    </div>
                    <p className="mt-4 text-dark fw-bold">
                        {UI_STRINGS.TOTAL_SELECTED(schedules.length)}
                    </p>
                </>
            ),
            confirmText: UI_STRINGS.DELETE_CONFIRM,
            cancelText: UI_STRINGS.DELETE_CANCEL,
            type: "danger",
            onConfirm: async () => {
                setIsDeleting(true);
                setDeleteDone(0);
                const ids = schedules.map(scheduleIdOf);
                let failed = 0;

                try {
                    for (const id of ids) {
                        try {
                            await deleteOneMaintenanceSchedule({
                                variables: { id },
                            });
                        } catch (err) {
                            failed += 1;
                            starekContext.showError(err);
                        } finally {
                            setDeleteDone((d) => d + 1);
                        }
                    }
                    if (failed < ids.length) {
                        starekContext.showSuccess(
                            `Deleted ${ids.length - failed} maintenance schedule(s)`,
                        );
                    }
                } finally {
                    if (failed === 0) deletedIdsRef.current = new Set(ids);
                    setIsDeleting(false);
                    setSelectedRows([]);
                    setToggleCleared((p) => !p);
                    await hardRefresh();
                    closeConfirm();
                    onSettled?.(failed === 0);
                }
            },
            onClose: () => {
                closeConfirm();
                onSettled?.(false);
            },
        });
    };

    const handleToggleActive = async (rows, makeActive) => {
        const ids = rows.map(scheduleIdOf);
        if (ids.length === 0) return;
        try {
            await updateManyMaintenanceSchedule({
                variables: {
                    data: makeActive ? restoreData() : deactivateData(),
                    where: { id: { in: ids } },
                },
            });
            starekContext.showSuccess(
                makeActive
                    ? `Restored ${ids.length} maintenance schedule(s)`
                    : `Deactivated ${ids.length} maintenance schedule(s)`,
            );
            setSelectedRows([]);
            setToggleCleared((p) => !p);
            await hardRefresh();
        } catch (err) {
            starekContext.showError(err);
        }
    };

    const handleToggleActiveSingle = async (schedule) => {
        const id = scheduleIdOf(schedule);
        try {
            const deactivating = schedule.is_active !== false;
            await updateManyMaintenanceSchedule({
                variables: {
                    data: deactivating ? deactivateData() : restoreData(),
                    where: { id: { in: [id] } },
                },
            });
            starekContext.showSuccess(
                deactivating
                    ? `${schedule.name} is deactivated`
                    : `${schedule.name} is restored`,
            );
            await hardRefresh();
        } catch (err) {
            starekContext.showError(err);
        }
    };

    // Listing rows carry the schedule under `.schedule`; route/sidebar rows are the schedule.
    const rowIsActive = (r) => (r.schedule ?? r).is_active !== false;
    const scheduleExtendedActions = selectedRows.length > 0 ? [
        {
            label: "Deactivate",
            icon: "fa-ban",
            danger: true,
            disabled: selectedRows.every((r) => !rowIsActive(r)),
            onClick: (rows) => handleToggleActive(rows.filter(rowIsActive), false),
        },
        {
            label: "Restore",
            icon: "fa-undo",
            disabled: selectedRows.every(rowIsActive),
            onClick: (rows) => handleToggleActive(rows.filter((r) => !rowIsActive(r)), true),
        },
    ] : [];

    // Only the page's first load shows the full-page loader; a Show Inactive toggle, a
    // refetch or a view switch keeps the page mounted (the operation bar holds the
    // search box text), so the sidebar and dialog are not torn down mid-action.
    if (activeInitialLoading && !hasLoaded) return <EntityLoadingState icon="fa-cogs" />;

    // Full-page error only before anything has loaded; later failures (page change,
    // search, view switch, refetch) keep the page and show an inline alert.
    return error && !hasLoaded ? (
        <div className="tw-text-center tw-mt-10 tw-text-red-600">
            Failed to load maintenance schedules: {error.message}
        </div>
    ) : (
        <div className="container-fluid">
            <div className="font-weight-bold mb-3">{UI_STRINGS.PAGE_TITLE}</div>
            {error && (
                <div className="alert alert-danger py-2 small">
                    Could not refresh maintenance schedules: {error.message}
                </div>
            )}

            <EntityOperationBar
                entity={{
                    singular: UI_STRINGS.TOOLBAR_ENTITY,
                    plural: UI_STRINGS.TOOLBAR_ENTITY,
                }}
                searchPlaceholder={UI_STRINGS.SEARCH_PLACEHOLDER}
                allowSearch={true}
                allowAdd={canWrite}
                allowDelete={canDestroy}
                allowEdit={canWrite}
                allowExtendedActions={canWrite && selectedRows.length > 0}
                extendedActions={scheduleExtendedActions}
                allowShowInactive={true}
                showInactive={showInactive}
                onShowInactiveChange={(v) => {
                    setShowInactive(v);
                    setSelectedRows([]);
                }}
                defaultView={viewMode}
                selectedRows={selectedRows}
                isDeleting={isDeleting}
                deleteDone={deleteDone}
                onSearch={(text) => setFilterText(text)}
                onAdd={handleAdd}
                onViewChange={(mode) => {
                    setViewMode(mode);
                    // Merge, don't replace — replacing drops showInactive.
                    patchSearchParams({ view: mode });
                    setSelectedRows([]);
                }}
                onEdit={handleEdit}
                onDelete={() => handleDelete(selectedRows)}
            >
                <EntityOperationBar.View
                    mode="calendar"
                    icon="fa fa-calendar"
                    tooltip="Calendar View"
                >
                    <MaintenanceSchedulesCalendarView
                        devices={devices}
                        selectedRows={selectedRows}
                        onSelectedRowsChange={(state) =>
                            setSelectedRows(state.selectedRows)
                        }
                        onRefresh={hardRefresh}
                        onEditSchedule={handleEditSchedule}
                        filterText={filterText}
                    />
                </EntityOperationBar.View>

                <EntityOperationBar.View
                    mode="list"
                    icon="fa fa-list"
                    tooltip="List View"
                >
                    <MaintenanceSchedulesListingView
                        items={pageItems}
                        selectedRows={selectedRows}
                        onSelectedRowsChange={(state) =>
                            setSelectedRows(state.selectedRows)
                        }
                        toggleCleared={toggleCleared}
                        loading={pageInitialLoading}
                        noDataComponent={<EntityEmptyState icon="fa-cogs" entityName="Maintenance Schedule" messages={EMPTY_MESSAGES.maintenanceSchedules} isFiltered={!!filterText.trim()} onAdd={handleAdd} />}
                        onRowClicked={handleRowClicked}
                        onSort={setSort}
                    />
                </EntityOperationBar.View>
            </EntityOperationBar>

            {isListView && (
                <Paginator
                    page={page}
                    pageCount={pageCount}
                    pageSize={pageSize}
                    total={total}
                    onChange={setPage}
                    disabled={pageLoading}
                />
            )}

            <MaintenanceScheduleSidebar
                show={showSidebar}
                onDiscard={handleSidebarClose}
                item={routeEditSchedule || selectedRows[0] || null}
                plantId={starekContext.lastUsedPlant.id}
                // The calendar's query already holds the plant's devices. In the list
                // view it is not running, and the sidebar loads the device list itself
                // whenever none is passed in.
                devices={isListView ? null : devices}
                onSave={handleSaveMaintenanceSchedule}
                onDelete={(schedule) =>
                    new Promise((resolve) => handleDelete([schedule], resolve))
                }
                onToggleActive={canWrite ? handleToggleActiveSingle : undefined}
            />

            <ConfirmModal
                open={confirmCfg.open}
                title={confirmCfg.title}
                confirmText={confirmCfg.confirmText}
                cancelText={confirmCfg.cancelText}
                type={confirmCfg.type}
                loading={isDeleting}
                onConfirm={confirmCfg.onConfirm}
                onClose={confirmCfg.onClose}
            >
                {confirmCfg.content}
            </ConfirmModal>
        </div>
    );
};

export { MaintenanceSchedules as Maintenance };
